/**
 * Exchange client for trading operations
 * Follows DexClient pattern from pkg/netrunner/clients/dex_client.go
 * Handles EIP-712 signatures and automatic satoshi conversion
 */

import { HttpClient } from "../../transport/http/HttpClient";
import { toSatoshi } from "../../utils/math";
import mormSigner from "../../signing/mormSigner";
import { NonceManager } from "../../utils/nonceManager";
import { InfoClient } from "../info/InfoClient";
import { fromSatoshi } from "../../utils/math";
import { shouldRefreshNonce } from "../../utils/errorDetection";
import { BaseSignatureBuilder, NonceProvider } from "./BaseSignatureBuilder";
import { toRFC3339Nano } from "../../utils/time";
import {
  OpSubmitOrder,
  OpCancelOrder,
  OpModifyOrder,
  OpUpdatePositionLeverage,
  OpClosePosition,
  OpSwap,
  OpChangeMarginRatio,
  OpInternTransfer,
  OpBankBucketTransfer,
  OpBankExtractToBucket,
  OpBankWithdrawFromBucket,
  OpBankMint,
  OpBankTransfer,
  OpCreateMarginBucket,
  ExchangeClientConfig,
  PlaceOrderParams,
  CancelOrderParams,
  ModifyOrderParams,
  ChangeLeverageParams,
  ClosePositionParams,
  SwapParams,
  ChangeMarginRatioParams,
  InternTransferParams,
  BucketTransferParams,
  ExtractToBucketParams,
  WithdrawFromBucketParams,
  BankMintParams,
  BankTransferParams,
  CreateBucketParams,
  PlaceOrderResponse,
  CancelOrderResponse,
  ModifyOrderResponse,
  ChangeLeverageResponse,
  ClosePositionResponse,
  ChangeMarginRatioResponse,
  InternTransferResponse,
  BucketTransferResponse,
  ExtractToBucketResponse,
  WithdrawFromBucketResponse,
  BankMintResponse,
  BankTransferResponse,
  CreateBucketResponse,
} from "../../types";

export class ExchangeClient {
  private httpClient: HttpClient;
  private infoClient: InfoClient;
  private nonceManager: NonceManager;
  private mnemonic: string;
  private chainID: number;
  private walletAddress?: string;
  private nonceCounter: number | null = null; // null = not initialized, will query on first use
  private signatureBuilder?: BaseSignatureBuilder;

  constructor(config: ExchangeClientConfig) {
    this.httpClient = new HttpClient(config.baseURL, config.maxRetries || 3);
    this.infoClient = new InfoClient({
      baseURL: config.baseURL,
    });
    this.nonceManager = new NonceManager(this.infoClient);
    this.mnemonic = config.mnemonic;
    this.chainID = config.chainID || 10900; // Default testnet chain ID (stored for future use)
  }

  /**
   * Get or initialize signature builder
   * Must be called after initialize() to ensure walletAddress is set
   */
  private getSignatureBuilder(): BaseSignatureBuilder {
    if (!this.signatureBuilder) {
      if (!this.walletAddress) {
        throw new Error(
          "ExchangeClient not initialized. Call initialize() first.",
        );
      }
      this.signatureBuilder = new BaseSignatureBuilder({
        mnemonic: this.mnemonic,
        walletAddress: this.walletAddress,
        chainID: this.chainID,
      });
    }
    return this.signatureBuilder;
  }

  /**
   * Get nonce provider adapter for BaseSignatureBuilder
   */
  private getNonceProvider(): NonceProvider {
    return {
      getNextNonce: () => this.getNextNonce(),
    };
  }

  /**
   * Initialize wallet address from mnemonic
   * Must be called before trading operations
   */
  async initialize(): Promise<void> {
    await mormSigner.initialize();
    // Trim mnemonic to handle whitespace issues (common with copy/paste)
    const trimmedMnemonic = this.mnemonic.trim();
    if (!trimmedMnemonic) {
      throw new Error("Mnemonic phrase is required");
    }
    const result = await mormSigner.generateAddress(trimmedMnemonic, 0);
    if (!result.success || !result.address) {
      throw new Error(`Failed to generate wallet address: ${result.error}`);
    }
    this.walletAddress = result.address;
    // Update mnemonic to trimmed version for consistency
    this.mnemonic = trimmedMnemonic;
    // Initialize signature builder now that we have walletAddress
    this.signatureBuilder = new BaseSignatureBuilder({
      mnemonic: this.mnemonic,
      walletAddress: this.walletAddress,
      chainID: this.chainID,
    });
  }

  /**
   * Get current wallet address
   */
  getWalletAddress(): string {
    if (!this.walletAddress) {
      throw new Error(
        "ExchangeClient not initialized. Call initialize() first.",
      );
    }
    return this.walletAddress;
  }

  /**
   * Refresh nonce from server (called on nonce errors)
   * Public method for use in retry logic
   */
  async refreshNonceFromServer(): Promise<void> {
    const walletAddress = this.getWalletAddress();
    const nonceResult = await this.nonceManager.getExpectedNextNonce(
      walletAddress,
      false,
    );
    this.nonceCounter = nonceResult.next_nonce;
    console.log("[ExchangeClient] Refreshed nonce from server", {
      walletAddress,
      newNonce: this.nonceCounter,
    });
  }

  /**
   * Get next nonce (lazy initialization from server, then increments locally)
   * Only queries server on first use or after refresh
   */
  private async getNextNonce(): Promise<string> {
    // Lazy initialization: query server on first use
    if (this.nonceCounter === null) {
      await this.refreshNonceFromServer();
    }

    // Increment and return
    this.nonceCounter!++;
    return this.nonceCounter!.toString();
  }

  /**
   * Build order submission payload following buildOrderSubmissionPayload pattern
   * Creates payload with action, marketId, bucketId, side, orderType, price, quantity
   * Converts price and quantity from decimal numbers to satoshi strings
   */
  private buildOrderPayload(params: PlaceOrderParams): Record<string, any> {
    // Convert price and quantity from decimal numbers to satoshi strings
    const priceStr = toSatoshi(params.price);
    const quantityStr = toSatoshi(params.quantity);

    // Build payload following buildOrderSubmissionPayload pattern
    const payload: Record<string, any> = {
      action: OpSubmitOrder,
      marketId: params.marketId,
      bucketId: params.bucketId, // Required: Bucket ID for order placement
      side: params.side.toUpperCase(),
      orderType: params.orderType.toUpperCase(),
      price: priceStr, // Satoshi format string
      quantity: quantityStr, // Satoshi format string
    };

    // Add optional advanced parameters (all in satoshi format)
    if (params.leverage) {
      payload.leverage = params.leverage;
    }
    if (params.takeProfit) {
      payload.takeProfit = params.takeProfit;
    }
    if (params.stopLoss) {
      payload.stopLoss = params.stopLoss;
    }

    return payload;
  }

  /**
   * Place an order with automatic satoshi conversion
   * Follows SubmitOrder pattern from dex_client.go
   *
   * @param params - Order parameters (price/quantity are decimal numbers, converted to satoshi in buildOrderPayload)
   * @returns API response
   */
  async placeOrder(params: PlaceOrderParams): Promise<PlaceOrderResponse> {
    // Input validation (following Go implementation)
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    if (
      !params.side ||
      (params.side.toUpperCase() !== "BUY" &&
        params.side.toUpperCase() !== "SELL")
    ) {
      throw new Error("side must be BUY or SELL");
    }
    if (
      !params.orderType ||
      (params.orderType.toUpperCase() !== "LIMIT" &&
        params.orderType.toUpperCase() !== "MARKET")
    ) {
      throw new Error("orderType must be LIMIT or MARKET");
    }
    // Validate price and quantity (already numbers, no conversion needed)
    if (!params.price || params.price <= 0) {
      throw new Error("price must be positive");
    }
    if (!params.quantity || params.quantity <= 0) {
      throw new Error("quantity must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload
    const payload = this.buildOrderPayload(params);
    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );

    // Generate EIP-712 signature using generateSingleSignature (fixes recovery ID issue)
    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build flat HTTP request structure (following submitCLOBOrderFlat pattern)
    const timestamp = Math.floor(Date.now() / 1000) - 5; // 5 seconds in the past for clock skew
    const clientOrderId = `order-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    const isBuy = params.side.toUpperCase() === "BUY";

    const orderReq: Record<string, any> = {
      address: this.getWalletAddress(),
      asset_pair: params.marketId,
      market_id: params.marketId,
      bucket_id: params.bucketId, // Required: Bucket ID for order placement
      price: completeMessage.price,
      size: completeMessage.quantity, // Use "size" instead of "quantity" for HTTP request
      is_buy: isBuy,
      order_type: params.orderType.toUpperCase(),
      timestamp: timestamp,
      client_order_id: clientOrderId,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline, // Already in Unix seconds as string
      signature: signature,
      time_in_force: params.timeInForce || "GTC",
    };

    // Add advanced parameters if provided
    if (params.leverage) {
      orderReq.leverage = params.leverage;
    }
    if (params.takeProfit) {
      orderReq.take_profit = params.takeProfit;
    }
    if (params.stopLoss) {
      orderReq.stop_loss = params.stopLoss;
    }
    if (params.postOnly) {
      orderReq.post_only = true;
    }
    if (params.hidden) {
      orderReq.hidden = true;
    }
    if (params.displayQuantity) {
      orderReq.display_quantity = params.displayQuantity;
    }
    if (params.reduceOnly) {
      orderReq.reduce_only = true;
    }

    // Submit to /clob/v1/order endpoint
    const response = await this.httpClient.post("/clob/v1/order", orderReq);
    return response.body;
  }

  /**
   * Cancel an order
   * Follows CancelOrder pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async cancelOrder(params: CancelOrderParams): Promise<CancelOrderResponse> {
    // Input validation (following Go implementation)
    if (!params.orderId || params.orderId === "") {
      console.error(
        "[ExchangeClient.cancelOrder] Validation failed: orderId is empty",
        params,
      );
      throw new Error("orderId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      console.error(
        "[ExchangeClient.cancelOrder] Validation failed: marketId is empty",
        params,
      );
      throw new Error("marketId cannot be empty");
    }

    console.log("[ExchangeClient.cancelOrder] Input validation passed", {
      orderId: params.orderId,
      marketId: params.marketId,
    });

    if (!this.walletAddress) {
      console.log(
        "[ExchangeClient.cancelOrder] Wallet not initialized, initializing now...",
      );
      await this.initialize();
      console.log("[ExchangeClient.cancelOrder] Wallet initialized", {
        walletAddress: this.walletAddress,
      });
    } else {
      console.log("[ExchangeClient.cancelOrder] Using existing wallet", {
        walletAddress: this.walletAddress,
      });
    }

    // Build payload following buildCancelOrderPayload pattern
    // EIP712 types: action, owner, address, orderId, marketId, nonce, deadline (7 fields)
    const walletAddress = this.getWalletAddress();
    const payload = {
      action: OpCancelOrder,
      orderId: params.orderId,
      marketId: params.marketId,
      address: walletAddress, // Required by EIP712 types
    };

    console.log("[ExchangeClient.cancelOrder] Payload built", {
      payload,
      action: OpCancelOrder,
    });

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    console.log("[ExchangeClient.cancelOrder] Complete message built", {
      action: completeMessage.action,
      orderId: completeMessage.orderId,
      marketId: completeMessage.marketId,
      address: completeMessage.address,
      owner: completeMessage.owner,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      hasAllFields: !!(
        completeMessage.action &&
        completeMessage.orderId &&
        completeMessage.marketId &&
        completeMessage.owner &&
        completeMessage.nonce &&
        completeMessage.deadline
      ),
    });

    // Generate signature using generateSingleSignature (fixes recovery ID issue)
    console.log("[ExchangeClient.cancelOrder] Generating signature...");
    const signature = await signatureBuilder.signPayload(completeMessage);
    console.log("[ExchangeClient.cancelOrder] Signature generated", {
      signatureLength: signature?.length,
      signaturePrefix: signature?.substring(0, 10) + "...",
    });

    // Build EIP712Tx structure (following buildEIP712Tx pattern)
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);
    console.log("[ExchangeClient.cancelOrder] EIP712Tx built", {
      eip712Tx: {
        txId: eip712Tx.txId,
        sigType: eip712Tx.sigType,
        threshold: eip712Tx.threshold,
        timestamp: eip712Tx.timestamp,
        deadline: eip712Tx.deadline,
        payloadAction: eip712Tx.payload?.action,
        payloadOrderId: eip712Tx.payload?.orderId,
        payloadMarketId: eip712Tx.payload?.marketId,
        signaturesCount: eip712Tx.signatures?.length,
        signer: eip712Tx.signatures?.[0]?.signer,
      },
    });

    // Submit to /router/v1/tx endpoint
    const baseURL = this.httpClient.getBaseURL
      ? this.httpClient.getBaseURL()
      : "unknown";
    console.log(
      "[ExchangeClient.cancelOrder] Submitting to /router/v1/tx endpoint...",
      {
        endpoint: "/router/v1/tx",
        baseURL,
        fullURL: `${baseURL}/router/v1/tx`,
      },
    );

    try {
      const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
      console.log("[ExchangeClient.cancelOrder] API response received", {
        status: response.statusCode,
        hasBody: !!response.body,
        responseBody: response.body,
        orderId: params.orderId,
      });
      return response.body;
    } catch (error) {
      // Check if this is a nonce error
      if (shouldRefreshNonce(error)) {
        console.log(
          "[ExchangeClient.cancelOrder] Nonce error detected, refreshing nonce from server",
          {
            error: error instanceof Error ? error.message : String(error),
            orderId: params.orderId,
          },
        );

        // Refresh nonce from server
        await this.refreshNonceFromServer();

        // Retry the request with new nonce
        const signatureBuilder = this.getSignatureBuilder();
        const retryCompleteMessage =
          await signatureBuilder.buildCompleteMessage(
            payload,
            this.getNonceProvider(),
          );
        const retrySignature =
          await signatureBuilder.signPayload(retryCompleteMessage);
        const retryEip712Tx = signatureBuilder.buildEIP712Tx(
          retryCompleteMessage,
          retrySignature,
        );

        console.log(
          "[ExchangeClient.cancelOrder] Retrying with refreshed nonce",
           retryCompleteMessage.nonce
        );

        const response = await this.httpClient.post(
          "/router/v1/tx",
          retryEip712Tx,
        );
        console.log("[ExchangeClient.cancelOrder] Retry successful", {
          status: response.statusCode,
          responseBody: response.body,
        });
        return response.body;
      }

      // Re-throw if not a nonce error
      console.error("[ExchangeClient.cancelOrder] API call failed", {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        orderId: params.orderId,
        marketId: params.marketId,
        walletAddress: this.walletAddress,
      });
      throw error;
    }
  }

  /**
   * Modify an order
   * Follows ModifyOrder pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async modifyOrder(params: ModifyOrderParams): Promise<ModifyOrderResponse> {
    // Input validation (following Go implementation)
    if (!params.orderId || params.orderId === "") {
      throw new Error("orderId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    // Validate price and quantity (already numbers, no conversion needed)
    if (!params.newPrice || params.newPrice <= 0) {
      throw new Error("newPrice must be positive");
    }
    if (!params.newQuantity || params.newQuantity <= 0) {
      throw new Error("newQuantity must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following buildModifyOrderPayload pattern
    // Convert price and quantity from decimal numbers to satoshi strings
    const payload: Record<string, any> = {
      action: OpModifyOrder,
      orderId: params.orderId,
      marketId: params.marketId,
      newPrice: toSatoshi(params.newPrice), // Convert to satoshi string
      newQuantity: toSatoshi(params.newQuantity), // Convert to satoshi string
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );

    // Generate signature using generateSingleSignature (fixes recovery ID issue)
    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build EIP712Tx structure (following buildEIP712Tx pattern)
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Change leverage for a position
   * Follows ChangeLeverage pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async changeLeverage(
    params: ChangeLeverageParams,
  ): Promise<ChangeLeverageResponse> {
    // Input validation
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (params.newLeverage === undefined) {
      throw new Error("newLeverage must be provided");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following bucket::update_position_leverage pattern
    // Convert newLeverage from decimal number to satoshi string
    // Use OpUpdatePositionLeverage instead of deprecated OpChangeLeverage
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    // CRITICAL: Do NOT include "address" in payload - only "owner" is in EIP712Types
    // bucketId is required (enforced by type system) and must not be empty
    if (params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }

    // Build payload for EIP712 signature (matches Go EIP712Types structure exactly)
    // EIP712Types expects exactly 8 fields: action, owner, marketId, newLeverage, bucketId, positionId, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    // CRITICAL: Do NOT include "leverageType" or "reason" in payload - they are not in EIP712Types
    // CRITICAL: Do NOT include "address" in payload - only "owner" is in EIP712Types
    // Payload should have exactly 5 fields: action, marketId, newLeverage, bucketId, positionId (buildCompleteMessage adds owner, nonce, deadline = 8 total)
    const payload: Record<string, any> = {
      action: OpUpdatePositionLeverage,
      marketId: params.marketId,
      newLeverage: toSatoshi(params.newLeverage), // Convert to satoshi string
      bucketId: params.bucketId, // Required for bucket operations
      positionId: params.positionId || "", // Position ID (required for EIP712Types, empty string if not provided)
    };
    
    // Debug: Verify payload has exactly 5 fields (will be 8 after buildCompleteMessage)
    console.log("[changeLeverage] Payload fields:", Object.keys(payload).length, Object.keys(payload));
    
    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    
    // Debug: Verify complete message has exactly 8 fields per Go EIP712Types
    const expectedFields = ["action", "owner", "marketId", "newLeverage", "bucketId", "positionId", "nonce", "deadline"];
    const actualFields = Object.keys(completeMessage);
    console.log("[changeLeverage] Complete message fields:", actualFields.length, actualFields);
    console.log("[changeLeverage] Expected fields (8):", expectedFields);
    console.log("[changeLeverage] Complete message:", JSON.stringify(completeMessage, null, 2));
    
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }
  /**
   * Close a position
   * Follows ClosePosition pattern from bucket/position_close_test.go
   * Uses /bucket/v1/position/close endpoint with EIP-712 signing
   *
   * EIP-712 payload structure (matching Go test):
   * - address (not owner)
   * - marketId (*big.Int as string)
   * - closeSize (*big.Int as string)
   * - marketPrice (*big.Int as string)
   * - nonce (uint64)
   * - deadline (uint64)
   * - NO action field in payload (domain is "bucket::close_position")
   *
   * Expected response structure (from position_close_test.go):
   * { success: boolean, error_message?: string, transactionHash?: string }
   */
  async closePosition(
    params: ClosePositionParams,
  ): Promise<ClosePositionResponse> {
    // Input validation
    if (!params.positionId || params.positionId.trim() === "") {
      throw new Error("positionId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!this.walletAddress) {
      await this.initialize();
    }

    const address = this.getWalletAddress();
    const now = Math.floor(Date.now() / 1000);
    const deadline = now + 300; // 5 minutes from now (matching Go test)
    const nonce = await this.getNextNonce();

    // Fetch market price if not provided (required for close position)
    let marketPriceValue: number;
    if (params.marketPrice && params.marketPrice > 0) {
      marketPriceValue = params.marketPrice;
    } else {
      // Fetch current market price from ticker
      try {
        const ticker = await this.infoClient.getMarketStats(params.marketId);
        if (!ticker || !ticker.last_price) {
          throw new Error(
            "marketPrice is required and could not be fetched from ticker",
          );
        }
        // Convert from satoshi string to decimal number
        marketPriceValue = fromSatoshi(ticker.last_price);
      } catch (error) {
        throw new Error(
          `Failed to fetch market price: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }

    // Convert market price to satoshi string
    const marketPrice = toSatoshi(marketPriceValue).toString();

    // Handle quantity: 0 or undefined means close full position (per Go test)
    // If quantity is provided and > 0, use it; otherwise use "0" for full close
    const closeSize =
      params.quantity && params.quantity > 0
        ? toSatoshi(params.quantity).toString()
        : "0"; // "0" means close full position per Go test (line 424, 429)

    // Build EIP-712 payload matching Go test structure exactly
    // EIP-712 schema: address, marketId, closeSize, marketPrice, nonce, deadline
    // NOTE: NO 'action' field in payload (domain "bucket::close_position" is handled by signing)
    // NOTE: NO 'owner' field - uses 'address' instead
    const eip712Payload: Record<string, any> = {
      action: OpClosePosition,
      address: address,
      marketId: params.marketId,
      closeSize: closeSize,
      marketPrice: marketPrice,
      nonce: nonce,
      deadline: deadline,
    };

    // Sign the payload using signPayload from BaseSignatureBuilder
    // The domain "bucket::close_position" is handled by the signing infrastructure
    const signatureBuilder = this.getSignatureBuilder();
    const signature = await signatureBuilder.signPayload(eip712Payload);

    // Build request body matching Go ClosePositionRequest structure exactly
    // Reference: bucket/position_close_test.go
    // JSON field names match protobuf json tags (snake_case)
    const closeRequest: Record<string, any> = {
      address: address, // User's blockchain address (for signature verification)
      position_id: params.positionId, // Position identifier (required by API)
      market_id: params.marketId, // uint64 as string
      close_size: closeSize, // u256 as satoshi-format string (1e8)
      market_price: marketPrice, // u256 as satoshi-format string (1e8)
      timestamp: now, // int64
      sign_type: 1, // EIP712 (v1.SignType enum)
      nonce: nonce.toString(), // string
      chain_id: this.chainID.toString(), // string
      deadline: deadline.toString(), // string
      signature: signature, // string
      // Optional multisig fields (not included in basic request)
      // multi_signatures: [],
      // threshold: 0,
    };

    // Submit to /bucket/v1/position/close endpoint (matching Go test)
    const response = await this.httpClient.post(
      "/bucket/v1/position/close",
      closeRequest,
    );
 
    // Response should match ClosePositionResponse structure:
    // { success: boolean, error_message?: string, transactionHash?: string, ... }
    return response.body as ClosePositionResponse;
  }

  /**
   * Close a position
   * Follows ClosePosition pattern from dex_client.go and bucket/position_close_test.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   *
   * Expected response structure (from position_close_test.go):
   * { success: boolean, error_message: string }
   *
   * Note: The Go test uses /bucket/v1/position/close endpoint with:
   * - closeSize (not quantity) in EIP-712 payload
   * - marketPrice (required) in EIP-712 payload
   * This implementation uses /router/v1/tx which may handle these differently.
   */
  async closePositionEIP712Tx(
    params: ClosePositionParams,
  ): Promise<ClosePositionResponse> {
    // Input validation
    if (!params.positionId || params.positionId.trim() === "") {
      throw new Error("positionId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!this.walletAddress) {
      await this.initialize();
    }
    // Validate and convert from decimal numbers to satoshi strings
    if (!params.quantity || params.quantity <= 0) {
      throw new Error("quantity must be positive");
    }
    if (!params.marketPrice || params.marketPrice <= 0) {
      throw new Error("marketPrice must be positive");
    }
    const close_size = toSatoshi(params.quantity);
    const market_price = toSatoshi(params.marketPrice);
    // Build payload
    // Note: Go test uses 'closeSize' and 'marketPrice' in EIP-712 payload,
    // but Router v1 endpoint may use 'quantity' instead of 'closeSize'
    const payload: Record<string, any> = {
      action: OpClosePosition,
      address: this.getWalletAddress(),
      positionId: params.positionId, // Position identifier (required by API)
      marketId: params.marketId,
      closeSize: close_size,
      marketPrice: market_price,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    // Note: Go test uses /bucket/v1/position/close, but Router v1 unified endpoint
    // should handle the same operation and return compatible response structure
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);

    // Response should match ClosePositionResponse structure:
    // { success: boolean, error_message?: string, ... }
    return response.body as ClosePositionResponse;
  }

  /**
   * Swap tokens
   * Follows Swap pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async swap(params: SwapParams): Promise<any> {
    // Input validation
    if (!params.fromToken || params.fromToken === "") {
      throw new Error("fromToken cannot be empty");
    }
    if (!params.toToken || params.toToken === "") {
      throw new Error("toToken cannot be empty");
    }
    // Validate amounts (already numbers, no conversion needed)
    if (!params.amountIn || params.amountIn <= 0) {
      throw new Error("amountIn must be positive");
    }
    if (!params.minAmountOut || params.minAmountOut <= 0) {
      throw new Error("minAmountOut must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateSwapPayload pattern
    // Convert amounts from decimal numbers to satoshi strings
    const payload: Record<string, any> = {
      action: OpSwap,
      fromToken: params.fromToken,
      toToken: params.toToken,
      amountIn: toSatoshi(params.amountIn), // Convert to satoshi string
      minAmountOut: toSatoshi(params.minAmountOut), // Convert to satoshi string
    };

    // Add optional path if provided
    if (params.path && params.path.length > 0) {
      payload.path = params.path;
    }

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Change margin ratio
   * Follows ChangeMarginRatio pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async changeMarginRatioEIP712Tx(
    params: ChangeMarginRatioParams,
  ): Promise<ChangeMarginRatioResponse> {
    // Input validation
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    // bucketId is required (enforced by EIP712Types)
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload for EIP712 signature (matches Go EIP712Types structure exactly)
    // EIP712Types expects exactly 8 fields: action, owner, marketId, bucketId, positionId, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    // CRITICAL: Do NOT include "address" in payload - only "owner" is in EIP712Types
    // Payload should have exactly 4 fields: action, marketId, bucketId, positionId (buildCompleteMessage adds owner, nonce, deadline = 8 total)
    const payload: Record<string, any> = {
      action: OpChangeMarginRatio,
      marketId: params.marketId,
      bucketId: params.bucketId, // Required for bucket operations
      positionId: params.positionId || "", // Position ID (required for EIP712Types, empty string if not provided)
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    
    // Debug: Verify complete message has exactly 8 fields per Go EIP712Types
    const expectedFields = ["action", "owner", "marketId", "bucketId", "positionId", "nonce", "deadline"];
    const actualFields = Object.keys(completeMessage);
    console.log("[changeMarginRatio] Complete message fields:", actualFields.length, actualFields);
    console.log("[changeMarginRatio] Expected fields (8):", expectedFields);
    console.log("[changeMarginRatio] Complete message:", JSON.stringify(completeMessage, null, 2));
    
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Internal transfer between accounts
   * Follows InternTransfer pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async internTransferEIP712Tx(
    params: InternTransferParams,
  ): Promise<InternTransferResponse> {
    // Input validation
    if (!params.fromAccount || params.fromAccount === "") {
      throw new Error("fromAccount cannot be empty");
    }
    if (!params.toAccount || params.toAccount === "") {
      throw new Error("toAccount cannot be empty");
    }
    if (!params.tokenAddress || params.tokenAddress === "") {
      throw new Error("tokenAddress cannot be empty");
    }
    // Validate amount (already number, no conversion needed)
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateInternTransferPayload pattern
    // Convert amount from decimal number to satoshi string
    const payload: Record<string, any> = {
      action: OpInternTransfer,
      fromAccount: params.fromAccount,
      toAccount: params.toAccount,
      amount: toSatoshi(params.amount), // Convert to satoshi string
      tokenAddress: params.tokenAddress,
      address: this.getWalletAddress(),
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Transfer margin between buckets
   * Follows the pattern from transfer_test.go
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   *
   * @param params - Bucket transfer parameters
   * @returns API response
   */
  async bucketTransfer(
    params: BucketTransferParams,
  ): Promise<BucketTransferResponse> {
    // Input validation
    if (!params.fromBucketId || params.fromBucketId.trim() === "") {
      throw new Error("fromBucketId cannot be empty");
    }
    if (!params.toBucketId || params.toBucketId.trim() === "") {
      throw new Error("toBucketId cannot be empty");
    }
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload for EIP712 signature (matches Go EIP712Types structure exactly)
    // EIP712Types expects exactly 8 fields: action, owner, fromBucketId, toBucketId, asset, amount, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    // CRITICAL: Do NOT include "address" in payload - only "owner" is in EIP712Types
    const payload: Record<string, any> = {
      action: OpBankBucketTransfer,
      fromBucketId: params.fromBucketId,
      toBucketId: params.toBucketId,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    
    // Debug: Verify all expected fields are present (exactly 8 fields per Go EIP712Types)
    const expectedFields = ["action", "owner", "fromBucketId", "toBucketId", "asset", "amount", "nonce", "deadline"];
    const actualFields = Object.keys(completeMessage);
    
    console.log("[bucketTransfer] Initial payload:", payload);
    console.log("[bucketTransfer] Complete message fields:", actualFields);
    console.log("[bucketTransfer] Complete message:", JSON.stringify(completeMessage, null, 2));
    console.log("[bucketTransfer] Expected fields (8):", expectedFields);
    console.log("[bucketTransfer] Actual field count:", actualFields.length, "expected:", expectedFields.length);
    
    // Ensure exactly 8 fields (no extra fields like address)
    if (actualFields.length !== expectedFields.length) {
      const extraFields = actualFields.filter(f => !expectedFields.includes(f));
      const missingFields = expectedFields.filter(f => !actualFields.includes(f));
      console.error("[bucketTransfer] Field count mismatch!");
      if (extraFields.length > 0) {
        console.error("[bucketTransfer] Extra fields (should be removed):", extraFields);
      }
      if (missingFields.length > 0) {
        console.error("[bucketTransfer] Missing fields:", missingFields);
      }
    }
    
    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build flat HTTP request structure (matches Go test reqBody structure)
    // HTTP request uses: address, source_bucket_id, target_bucket_id, amount, reason, timestamp, nonce, deadline, signature, chain_id
    const timestamp = Math.floor(Date.now() / 1000);
    const reqBody: Record<string, any> = {
      address: walletAddress,
      source_bucket_id: params.fromBucketId, // snake_case in HTTP body
      target_bucket_id: params.toBucketId, // snake_case in HTTP body
      amount: amountStr,
      reason: "sdk_transfer", // Optional field, present in Go test
      timestamp: timestamp,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature: signature,
      chain_id: this.chainID.toString(),
    };

    // Submit to /bucket/v1/bucket/transfer endpoint with flat structure
    const response = await this.httpClient.post(
      "/bucket/v1/bucket/transfer",
      reqBody,
    );
    return response.body;
  }

  /**
   * Extract spot balance to bucket (bank → bucket)
   * Follows the pattern from bank_to_bucket_test.go
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   *
   * @param params - Extract to bucket parameters
   * @returns API response
   */
  async extractToBucket(
    params: ExtractToBucketParams,
  ): Promise<ExtractToBucketResponse> {
    // Input validation
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    // Validate amount (already number, no conversion needed)
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload for EIP712 signature (matches Go EIP712Types structure exactly)
    // EIP712Types expects exactly 7 fields: action, owner, bucketId, asset, amount, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    // CRITICAL: Do NOT include "address" or "timestamp" in payload - only the 7 fields in EIP712Types
    const payload: Record<string, any> = {
      action: OpBankExtractToBucket,
      bucketId: params.bucketId,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    
    // Debug: Verify all expected fields are present (exactly 7 fields per Go EIP712Types)
    const expectedFields = ["action", "owner", "bucketId", "asset", "amount", "nonce", "deadline"];
    const actualFields = Object.keys(completeMessage);
    
    console.log("[extractToBucket] Initial payload:", payload);
    console.log("[extractToBucket] Complete message fields:", actualFields);
    console.log("[extractToBucket] Complete message:", JSON.stringify(completeMessage, null, 2));
    console.log("[extractToBucket] Expected fields (7):", expectedFields);
    console.log("[extractToBucket] Actual field count:", actualFields.length, "expected:", expectedFields.length);
    
    // Ensure exactly 7 fields (no extra fields like timestamp)
    if (actualFields.length !== expectedFields.length) {
      const extraFields = actualFields.filter(f => !expectedFields.includes(f));
      const missingFields = expectedFields.filter(f => !actualFields.includes(f));
      console.error("[extractToBucket] Field count mismatch!");
      if (extraFields.length > 0) {
        console.error("[extractToBucket] Extra fields (should be removed):", extraFields);
      }
      if (missingFields.length > 0) {
        console.error("[extractToBucket] Missing fields:", missingFields);
      }
    }
    
    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build flat HTTP request structure (matches Go test reqBody structure)
    // HTTP request uses: address, bucket_id (snake_case), asset, amount, timestamp, nonce, deadline, signature, chain_id
    // Use RFC3339Nano format for timestamp (matching Go: time.Now().UTC().Format(time.RFC3339Nano))
    const timestamp = toRFC3339Nano();
    const reqBody: Record<string, any> = {
      address: walletAddress,
      bucket_id: params.bucketId, // snake_case in HTTP body
      asset: params.assetId, // Use "asset" not "assetId"
      amount: amountStr,
      timestamp: timestamp, // RFC3339Nano format (e.g., "2025-12-25T18:55:56.123456789Z")
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature: signature,
      chain_id: this.chainID.toString(),
    };

    // Submit to /bank/v1/extract-to-bucket endpoint with flat structure
    const response = await this.httpClient.post(
      "/bank/v1/extract-to-bucket",
      reqBody,
    );
    return response.body;
  }

  /**
   * Create a margin bucket
   * Follows the pattern from create_bucket_test.go
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   *
   * @param params - Create bucket parameters
   * @returns API response
   */
  async createBucket(
    params: CreateBucketParams,
  ): Promise<CreateBucketResponse> {
    // Input validation
    if (!params.bucketType || params.bucketType.trim() === "") {
      throw new Error("bucketType cannot be empty");
    }
    if (!params.marginType || params.marginType.trim() === "") {
      throw new Error("marginType cannot be empty");
    }
    const marginTypeUpper = params.marginType.toUpperCase();
    if (marginTypeUpper !== "CROSS" && marginTypeUpper !== "ISOLATED") {
      throw new Error("marginType must be 'cross' or 'isolated'");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();

    // Use RFC3339Nano format for timestamp (matching Go test: time.Now().UTC().Format(time.RFC3339Nano))
    const timestamp = toRFC3339Nano();

    // Build payload for EIP712 signature (matches Go EIP712Types structure)
    // EIP712Types expects (in order): action, owner, bucketType, marginType, asset, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    // asset is optional and defaults to "USDC" if omitted, but MUST be present in payload
    // CRITICAL: All 7 fields must be present for Go EIP712Types validation
    const assetValue =
      params.asset && params.asset.trim() !== "" ? params.asset : "USDC";
    const payload: Record<string, any> = {
      action: OpCreateMarginBucket,
      bucketType: params.bucketType,
      marginType: marginTypeUpper.toLowerCase(), // Use lowercase in payload
      asset: assetValue, // Required in EIP-712 payload (defaults to "USDC" if omitted or empty)
    };

    // Final validation: ensure asset is always present and is a non-empty string
    if (
      !payload.asset ||
      typeof payload.asset !== "string" ||
      payload.asset.trim() === ""
    ) {
      payload.asset = "USDC";
    }

    const signatureBuilder = this.getSignatureBuilder();
    // Use 3600 seconds (1 hour) deadline offset to match Go test
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      3600, // 1 hour from now (matching Go test: time.Now().Unix() + 3600)
    );

    // Debug: Verify all 7 fields are present (matching Go EIP712Types exactly)
    const expectedFields = [
      "action",
      "owner",
      "bucketType",
      "marginType",
      "asset",
      "nonce",
      "deadline",
    ];
    const actualFields = Object.keys(completeMessage);
    const missingFields = expectedFields.filter(
      (f) => !actualFields.includes(f),
    );

    console.log("[createBucket] Initial payload:", payload);
    console.log("[createBucket] Complete message fields:", actualFields);
    console.log(
      "[createBucket] Complete message:",
      JSON.stringify(completeMessage, null, 2),
    );
    console.log("[createBucket] Expected fields:", expectedFields);
    console.log(
      "[createBucket] Field count:",
      actualFields.length,
      "expected:",
      expectedFields.length,
    );

    if (missingFields.length > 0) {
      console.error("[createBucket] Missing fields in payload:", missingFields);
      console.error("[createBucket] Actual payload fields:", actualFields);
    }

    // Ensure all required fields are present
    if (actualFields.length !== expectedFields.length) {
      throw new Error(
        `Payload field count mismatch: expected ${expectedFields.length} fields ` +
          `(action, owner, bucketType, marginType, asset, nonce, deadline), ` +
          `but got ${actualFields.length} fields: ${actualFields.join(", ")}`,
      );
    }

    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build flat HTTP request structure (matches Go CreateMarginBucketRequest structure)
    // HTTP request uses: address, bucket_id (optional), bucket_type, margin_type, initial_margin, timestamp, nonce, deadline, signature, chain_id
    const reqBody: Record<string, any> = {
      address: walletAddress,
      bucket_type: params.bucketType, // snake_case in HTTP body
      margin_type: marginTypeUpper.toLowerCase(), // lowercase in HTTP body
      initial_margin: params.initialMargin || "0", // Default to "0" if not provided (matching Go test)
      timestamp: timestamp, // RFC3339Nano format (matching Go test)
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature: signature,
      chain_id: this.chainID.toString(),
    };

    // Include bucket_id if provided (optional, may be auto-generated by server)
    if (params.bucketId) {
      reqBody.bucket_id = params.bucketId;
    }

    // Submit to /bucket/v1/bucket endpoint with flat structure
    const response = await this.httpClient.post("/bucket/v1/bucket", reqBody);
    return response.body;
  }

  /**
   * Withdraw margin from bucket to bank (bucket → bank)
   * Follows the same pattern as extractToBucket
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   *
   * @param params - Withdraw from bucket parameters
   * @returns API response
   */
  async withdrawFromBucket(
    params: WithdrawFromBucketParams,
  ): Promise<WithdrawFromBucketResponse> {
    // Input validation
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload for EIP712 signature (matches Go EIP712Types structure)
    // EIP712Types expects: action, owner, bucketId, asset, amount, nonce, deadline
    // Note: owner, nonce, and deadline are added by buildCompleteMessage
    // CRITICAL: Do NOT include "address" in payload - only "owner" is in EIP712Types
    const payload: Record<string, any> = {
      action: OpBankWithdrawFromBucket,
      bucketId: params.bucketId,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build flat HTTP request structure (matches Go test reqBody structure)
    // HTTP request uses: address, bucket_id (snake_case), asset, amount, timestamp, nonce, deadline, signature, chain_id
    // Use RFC3339Nano format for timestamp (matching Go: time.Now().UTC().Format(time.RFC3339Nano))
    const timestamp = toRFC3339Nano();
    const reqBody: Record<string, any> = {
      address: walletAddress,
      bucket_id: params.bucketId, // snake_case in HTTP body
      asset: params.assetId, // Use "asset" not "assetId"
      amount: amountStr,
      timestamp: timestamp, // RFC3339Nano format (e.g., "2025-12-25T18:55:56.123456789Z")
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature: signature,
      chain_id: this.chainID.toString(),
    };

    // Submit to /bank/v1/withdraw-from-bucket endpoint with flat structure
    const response = await this.httpClient.post(
      "/bank/v1/withdraw-from-bucket",
      reqBody,
    );
    return response.body;
  }

  /**
   * Mint new tokens
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   *
   * @param params - Bank mint parameters
   * @returns API response
   */
  async bankMint(params: BankMintParams): Promise<BankMintResponse> {
    // Input validation
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload following bank::mint EIP712 types
    // Payload uses: owner, address, asset, amount, nonce, deadline
    // address is required for signature verification
    const payload: Record<string, any> = {
      action: OpBankMint,
      address: this.getWalletAddress(), // Required for signature verification
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Direct address-to-address asset transfer
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   *
   * @param params - Bank transfer parameters
   * @returns API response
   */
  async bankTransfer(
    params: BankTransferParams,
  ): Promise<BankTransferResponse> {
    // Input validation
    if (!params.to || params.to.trim() === "") {
      throw new Error("to address cannot be empty");
    }
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    // Validate amount (already number, no conversion needed)
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload following bank::transfer EIP712 types
    // Payload uses: owner, address, to, asset, amount, nonce, deadline
    // address is required for signature verification
    const payload: Record<string, any> = {
      action: OpBankTransfer,
      address: this.getWalletAddress(), // Required for signature verification
      to: params.to,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }
}
